import { NgClass, NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    ChangeDetectorRef,
    ViewChild,
} from '@angular/core';

import { Router } from '@angular/router';

import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';

import { VenueService } from 'app/shared/services/venue.service';
import { ApexOptions, NgApexchartsModule } from 'ng-apexcharts';
import { Subject, takeUntil } from 'rxjs';
import { VenueComponent } from './venue/venue.component';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'app-venues',
    templateUrl: './venues.component.html',
    styleUrl: './venues.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatDividerModule,
        NgApexchartsModule,
        MatTableModule,
        MatSortModule,
        MatPaginatorModule,
        NgClass,
        NgIf,
        MatProgressBarModule,
        MatFormFieldModule,
        MatSidenavModule,
        MatTooltipModule,
        VenueComponent,
    ],
})
export class VenuesComponent implements OnInit, OnDestroy {
    @ViewChild(VenueComponent) venueEditor?: VenueComponent;

    data: any;
    accountBalanceOptions: ApexOptions;
    recentVenuesDataSource: MatTableDataSource<any> = new MatTableDataSource();
    recentVenuesTableColumns: string[] = [
        'name',
        'location',
        'status',
        'actions',
    ];
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    drawerMode: 'over' | 'side' = 'over';
    drawerOpened: boolean = false;

    selectedVenue: Object | null = null;
    pageIndex = 0;
    pageSize = 10;
    totalItems = 0;
    sortBy = 'name:asc';

    constructor(
        private _fuseConfirmationService: FuseConfirmationService,
        private _router: Router,
        private venueService: VenueService,
        private cdr: ChangeDetectorRef,
        private snackBar: MatSnackBar,
    ) {}

    ngOnInit(): void {
        this.getVenues();
        this.cdr.markForCheck();
    }

    onBackdropClicked(): void {
        if (!this.venueEditor?.hasUnsavedChanges()) {
            this.drawerOpened = false;
            this.cdr.markForCheck();
            return;
        }

        const dialogRef = this._fuseConfirmationService.open({
            title: 'Discard unsaved changes?',
            message:
                'Your venue changes, including the selected location, have not been saved.',
            actions: {
                confirm: { label: 'Discard' },
                cancel: { label: 'Keep editing' },
            },
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result) => {
                if (result === 'confirmed') {
                    this.drawerOpened = false;
                    this.selectedVenue = null;
                }
                this.cdr.markForCheck();
            });
    }

    viewVenue(venue: Object): void {
        this._router.navigate(['./venue', (venue as any).id]);
        this.cdr.markForCheck();
    }

    getVenues(): void {
        this.venueService
            .getVenuesPage(this.pageIndex + 1, this.pageSize, this.sortBy)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    const venues = response?.results ?? [];
                    this.totalItems = response?.totalResults ?? 0;
                    this.recentVenuesDataSource.data = venues.map((v) => ({
                        id: v._id,
                        _id: v._id,
                        name: v.name,
                        location: v.location ? v.location : '',
                        timing: v.timing,
                        status: v.status,
                        cancellationPolicy: v.cancellationPolicy || null,
                        privacyPolicy: v.privacyPolicy || null,
                    }));
                    this.cdr.markForCheck();
                },
                error: (err) => {
                    console.error('Error fetching venues:', err);
                    this.cdr.markForCheck();
                },
            });
    }

    onPageChange(event: PageEvent): void {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;
        this.getVenues();
    }

    onSortChange(sort: Sort): void {
        this.pageIndex = 0;
        this.sortBy = sort.direction
            ? `${sort.active}:${sort.direction}`
            : 'name:asc';
        this.getVenues();
    }

    editVenue(venue: Object): void {
        const venueData = venue as any;

        // Always fetch full venue details to ensure we have the latest data including policies
        if (venueData && (venueData.id || venueData._id)) {
            // Reset selectedVenue first to trigger change detection
            this.selectedVenue = null;
            this.drawerOpened = false;
            this.cdr.markForCheck();

            const venueId = venueData.id || venueData._id;
            this.venueService
                .getVenueById(venueId)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                    next: (fullVenue) => {
                        console.log('=== API Response ===', fullVenue);

                        // Handle case where API might return wrapped response
                        const venueData =
                            fullVenue.results || fullVenue.data || fullVenue;

                        // Use the full venue data from API
                        this.selectedVenue = {
                            ...venueData,
                            id: venueData._id || venueData.id || venueId,
                            _id: venueData._id || venueData.id || venueId,
                        };

                        console.log(
                            '=== Selected Venue Set To ===',
                            this.selectedVenue,
                        );

                        // Open drawer after data is set
                        setTimeout(() => {
                            this.drawerOpened = true;
                            this.cdr.markForCheck();
                        }, 100);
                    },
                    error: (err) => {
                        console.error('Error fetching venue details:', err);
                        // Fallback to original venue data
                        this.selectedVenue = {
                            ...venueData,
                            id: venueData.id || venueData._id,
                            _id: venueData._id || venueData.id,
                        };
                        setTimeout(() => {
                            this.drawerOpened = true;
                            this.cdr.markForCheck();
                        }, 0);
                    },
                });
        } else {
            // No venue ID, it's a new venue
            this.selectedVenue = null;
            this.drawerOpened = true;
            this.cdr.markForCheck();
        }
    }

    close($event): void {
        this.drawerOpened = false;
        this.selectedVenue = null;
        this.getVenues();
        this.cdr.markForCheck();
    }

    delete(id: string): void {
        const dialogRef = this._fuseConfirmationService.open({
            title: 'Remove Venue',
            message:
                'Are you sure you want to remove this Venue permanently? <span class="font-medium">This action cannot be undone!</span>',
        });

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                this.venueService
                    .delete(id)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe({
                        next: (res) => {
                            this.getVenues();
                            this.cdr.markForCheck();
                        },
                        error: (err) => {
                            this.snackBar.open(
                                'Error deleting venue. Please try again.',
                                'Close',
                                {
                                    duration: 3000,
                                },
                            );
                            console.error('Error deleting venue:', err);
                            this.cdr.markForCheck();
                        },
                    });
            }
            this.cdr.markForCheck();
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        this.cdr.markForCheck();
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }
}
